import { useId, useState } from "react";

import { CHAPTER } from "../../../chapter";
import { friendlyError } from "../../../lib/friendlyError";
import { trpc } from "../../../trpc";
import { TestThenSend } from "../TestThenSend";
import { emailTypeLabel } from "../emailTypes";
import {
  EMAIL_OFF_SENTENCE,
  people,
  SEND_OFFLINE_SENTENCE,
  sendAllSentence,
  testSentSentence,
} from "../sendCopy";
import { BODY_TEXT, STEP_HEADING } from "../ui";
import { useSendFlow } from "../useSendFlow";

type Variant = "week" | "sunday" | "calendar";

const VERSIONS: { id: Variant; title: string; blurb: string }[] = [
  {
    id: "week",
    title: "The whole week",
    blurb:
      "One email showing the three main Sydney days: But, why?, the Yajna and the Sunday session.",
  },
  {
    id: "sunday",
    title: "The Sunday session",
    blurb:
      "An invitation to the Sunday session, with the rest of the week mentioned briefly.",
  },
  {
    id: "calendar",
    title: "Short note with a link",
    blurb: `A short note about the visit with one button to ${CHAPTER.siteHost}/events.`,
  },
];

/**
 * The September 2026 visit announcement: the widest send there is, to
 * every email address the database holds (bookings, members and website
 * messages), with an unsubscribe link on each. Pick a version, test it,
 * then send.
 */
export function SeptemberVisitPanel({
  defaultTestEmail,
  senderName,
}: {
  defaultTestEmail: string;
  senderName: string | null;
}) {
  const [variant, setVariant] = useState<Variant>("week");
  const flow = useSendFlow(defaultTestEmail);
  const groupLabelId = useId();

  const audience = trpc.adminCampaignAudienceCount.useQuery(undefined, {
    retry: false,
    staleTime: 60_000,
  });
  const utils = trpc.useUtils();
  const campaign = trpc.adminSendSeptemberCampaign.useMutation();

  const send = (mode: "test" | "all", testEmails: string[]) => {
    campaign.mutate(
      {
        variant,
        mode,
        testEmails: mode === "test" ? testEmails : undefined,
        testName: mode === "test" ? (senderName ?? undefined) : undefined,
      },
      {
        onSuccess: (res) => {
          if (res.mode === "test") {
            flow.testSent(
              res.skipped ? EMAIL_OFF_SENTENCE : testSentSentence(res.sentTo),
            );
          } else {
            flow.sendAllDone(
              sendAllSentence(
                res,
                "There's no one to send this to, so nothing was sent.",
              ),
            );
            void utils.adminListEmailSends.invalidate();
          }
        },
        onError: (err) =>
          flow.problem(friendlyError(err, SEND_OFFLINE_SENTENCE)),
      },
    );
  };

  const busy = campaign.isPending;
  const count = audience.data?.count ?? null;

  return (
    <>
      <p className={BODY_TEXT}>
        Tells everyone we have an email address for about the September visit:
        people who have booked, joined or written to us. Each email has an
        unsubscribe link. Pick a version, send yourself a test, then send to
        everyone.
      </p>
      <p className="mt-2 text-[15px] text-[color:var(--color-ink)]">
        {audience.isLoading
          ? "Counting who this goes to…"
          : audience.error
            ? "Couldn't count who this would go to. Refresh the page before sending."
            : `Goes to ${people(count ?? 0)}.`}
      </p>

      <p id={groupLabelId} className={`mt-5 ${STEP_HEADING}`}>
        Which version?
      </p>
      <div
        role="radiogroup"
        aria-labelledby={groupLabelId}
        className="mt-2 grid gap-2.5 md:grid-cols-3"
      >
        {VERSIONS.map((v) => {
          const active = variant === v.id;
          return (
            <button
              key={v.id}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={busy}
              onClick={() => {
                if (!active) {
                  setVariant(v.id);
                  flow.optionsChanged();
                }
              }}
              className={`min-h-11 rounded-lg border p-3.5 text-left transition-colors ${
                active
                  ? "border-2 border-[color:var(--color-amber)] bg-[color:var(--color-paper)]"
                  : "border-[color:var(--color-line)] hover:border-[color:var(--color-line-strong)]"
              }`}
            >
              <span className="block text-[15px] font-semibold text-[color:var(--color-ink)]">
                {v.title}
              </span>
              <span className="mt-1.5 block text-[14px] leading-relaxed text-[color:var(--color-ink-soft)]">
                {v.blurb}
              </span>
            </button>
          );
        })}
      </div>

      <TestThenSend
        flow={flow}
        emailName={
          emailTypeLabel(`campaign_${variant}`) ??
          "September visit announcement"
        }
        audience={count !== null && count > 0 ? people(count) : null}
        noAudienceText={
          audience.isLoading
            ? "Counting who this goes to…"
            : audience.error
              ? "Couldn't count who this would go to. Refresh the page before sending."
              : "There's no one to send this to yet."
        }
        multipleAddresses
        busy={busy}
        onSendTest={(addresses) => send("test", addresses)}
        onSendAll={() => send("all", [])}
      />
    </>
  );
}
